import { useState, useEffect } from 'react';

/**
 * Hook to handle gated content button props with dynamic URL resolution.
 *
 * Manages button properties (href, target, download) for buttons that may point to gated content forms.
 * When a button's href matches a gated form anchor target, automatically updates to use the gated file
 * download URL once available.
 *
 * **Dependency:** Depends on `window.timelyGatedFormDetails` being set by FormSection.js when a gated
 * form is unlocked (user logged in or previously unlocked). Polls every 100ms (up to 5 seconds) for this data.
 *
 * @param {Object} umbraco - The umbraco data object containing Button property with Url and Target
 * @returns {Object} buttonProps - Object containing href, target, and download properties
 */
export const useGatedButtonProps = (umbraco) => {
  const getButtonProps = () => {
    const defaultHref = umbraco?.Button?.Url;
    const defaultTarget = umbraco?.Button?.Target;
    const gated = window?.timelyGatedFormDetails;
    if (gated && defaultHref === '#' + gated.anchorTarget) {
      return {
        href: gated.gatedFileUrl || defaultHref,
        target: gated.gatedFileTarget || defaultTarget,
        download: null,
      };
    }
    return { href: defaultHref, target: defaultTarget, download: null };
  };

  // Determine the correct button props, use an interval to check if the information for the gated content is available
  // The Inter-Island Communication in our patterns makes it a bit complex to share state or coordination actions between components
  const [buttonProps, setButtonProps] = useState(getButtonProps);

  useEffect(() => {
    const startTime = Date.now();
    const maxDuration = 5000;
    const interval = 100;

    const intervalId = setInterval(() => {
      const elapsed = Date.now() - startTime;

      if (window?.timelyGatedFormDetails) {
        setButtonProps(getButtonProps);
        clearInterval(intervalId);
      } else if (elapsed >= maxDuration) {
        clearInterval(intervalId);
      }
    }, interval);

    return () => clearInterval(intervalId);
  }, []);

  return buttonProps;
};
